import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@common/components/ui/toggle-group"
import { type FieldValues, type Path, useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"
import type { Option } from "./select"

const CHIPS = "flex-wrap justify-start gap-1"

const CHIP = "h-7 rounded-md px-2 text-small-regular data-[state=on]:bg-muted"

type Props<T extends FieldValues, V extends string> = {
  name: Path<T>
  label: string
  options: ReadonlyArray<Option<V>>
  optional?: boolean
}

export function ChipsField<T extends FieldValues, V extends string>({
  name,
  label,
  options,
  optional,
}: Props<T, V>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FieldLabel label={label} optional={optional} />
          <FormControl>
            <ToggleGroup
              type="multiple"
              value={field.value}
              onValueChange={(next) =>
                field.onChange(
                  options
                    .filter((option) => next.includes(option.value))
                    .map((option) => option.value),
                )
              }
              className={CHIPS}
            >
              {options.map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={CHIP}
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function SingleChips<V extends string>({
  value,
  options,
  optional,
  onChange,
}: {
  value: V | null
  options: ReadonlyArray<Option<V>>
  optional?: boolean
  onChange: (next: V | null) => void
}) {
  return (
    <ToggleGroup
      type="single"
      value={value ?? ""}
      onValueChange={(next) => {
        const picked = options.find((option) => option.value === next)
        if (picked || optional) onChange(picked?.value ?? null)
      }}
      className={CHIPS}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={CHIP}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}

export function ChipField<T extends FieldValues, V extends string>({
  name,
  label,
  options,
  optional,
}: Props<T, V>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FieldLabel label={label} optional={optional} />
          <FormControl>
            <SingleChips
              value={field.value}
              options={options}
              optional={optional}
              onChange={field.onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function BareChipField<T extends FieldValues, V extends string>({
  name,
  options,
  optional,
}: Omit<Props<T, V>, "label">) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <SingleChips
          value={field.value}
          options={options}
          optional={optional}
          onChange={field.onChange}
        />
      )}
    />
  )
}
